import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getBackendUrl } from '../../api';
import Swal from 'sweetalert2';

/**
 * WorkerTickets — self-contained top-bar widget.
 *
 * ONE ticket icon (place after the bell). Clicking opens a modal with three tabs:
 *   • Raise a ticket   — category → assignee → priority → message → files
 *   • My tickets       — tickets the person raised (scope=mine), expandable
 *   • Assigned to me   — tickets assigned to the person (scope=assigned) with
 *                        status buttons; empty for plain employees
 *
 * Notifications: polls every 30s and shows a red dot + toast for two events —
 *   1. a ticket the person RAISED changes status, and
 *   2. a ticket is ASSIGNED to the person that they haven't reviewed yet.
 * The assigned dot reflects any unreviewed assignment (including ones made while
 * away); toasts are suppressed on the first poll so a fresh load doesn't stack
 * them. Opening the panel acknowledges everything, clears the dot, and lands on
 * the tab the dot was about.
 *
 * Backdrop click does NOT close the modal — X only.
 *
 * Usage in Worker.jsx top bar, right after the bell button:
 *     <WorkerTickets user={user} isMobile={isMobile} />
 */

const API = `${getBackendUrl()}/api/tickets`;

const PRIORITY = {
  high:   { label: 'High',   color: '#b91c1c', bg: '#fee2e2', rail: '#ef4444', sla: 'Response within 1 hour' },
  medium: { label: 'Medium', color: '#b45309', bg: '#fef3c7', rail: '#f59e0b', sla: 'Response within half a day' },
  low:    { label: 'Low',    color: '#1d4ed8', bg: '#dbeafe', rail: '#3b82f6', sla: 'Response within one day' },
};
const STATUS = {
  open:        { label: 'Open',        color: '#b45309', bg: '#fef3c7' },
  in_progress: { label: 'In progress', color: '#1d4ed8', bg: '#dbeafe' },
  resolved:    { label: 'Resolved',    color: '#15803d', bg: '#dcfce7' },
  closed:      { label: 'Closed',      color: '#4b5563', bg: '#e5e7eb' },
};

const IMAGE_ACCEPT = 'image/*';
const DOC_ACCEPT = '.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.ppt,.pptx';

/* ---------- tiny helpers ---------- */
function toast(msg, ok = true) {
  const s = typeof window !== 'undefined' ? window.Swal || Swal : null;
  if (s) {
    s.fire({
      toast: true, position: 'top-end', timer: 2600, showConfirmButton: false,
      icon: ok ? 'success' : 'error', title: msg,
    });
  } else if (!ok) {
    alert(msg);
  }
}

async function apiGet(path) {
  const r = await fetch(API + path, { credentials: 'include' });
  if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `HTTP ${r.status}`);
  return r.json();
}
async function apiPostForm(path, formData) {
  const r = await fetch(API + path, { method: 'POST', credentials: 'include', body: formData });
  if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `HTTP ${r.status}`);
  return r.json();
}
async function apiPost(path, body) {
  const r = await fetch(API + path, {
    method: 'POST', credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `HTTP ${r.status}`);
  return r.json();
}

/* ---------- small presentational bits ---------- */
function Pill({ map, k }) {
  const s = map[k] || { label: k, color: '#4b5563', bg: '#e5e7eb' };
  return (
    <span style={{
      display: 'inline-block', padding: '3px 10px', borderRadius: 999,
      fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
      color: s.color, background: s.bg,
    }}>{s.label}</span>
  );
}

export default function WorkerTickets({ user, isMobile = false }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('raise');       // 'raise' | 'mine' | 'assigned'
  const [unseenMine, setUnseenMine] = useState(0);          // status changes on tickets I raised
  const [unseenAssigned, setUnseenAssigned] = useState(0);  // new tickets assigned to me
  const unseen = unseenMine + unseenAssigned;               // drives the red dot
  // after: setUnseenMine(changed);
console.log('unseen:', unseen, 'unseenMine',unseenMine, 'unseenAssigned',unseenAssigned);

  /* raise form */
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState('');
  const [assignees, setAssignees] = useState([]);
  const [assignee, setAssignee] = useState('');
  const [priority, setPriority] = useState('medium');
  const [message, setMessage] = useState('');
  const [images, setImages] = useState([]);       // File[]
  const [docs, setDocs] = useState([]);           // File[]
  const [loadingA, setLoadingA] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  /* my tickets */
  const [myTickets, setMyTickets] = useState([]);
  const [loadingMine, setLoadingMine] = useState(false);
  const [expanded, setExpanded] = useState({});

  /* assigned to me */
  const [assignedTickets, setAssignedTickets] = useState([]);
  const [loadingAssigned, setLoadingAssigned] = useState(false);

  const detectedRef = useRef(new Set());          // fire each toast once
  const firstAssignedPollRef = useRef(true);      // suppress toast burst on the first poll only
  const snapKey   = user?.email ? `ticket_status_seen_${user.email}` : null;    // tickets I raised → status
  const assignKey = user?.email ? `ticket_assigned_seen_v2_${user.email}` : null;  // tickets assigned to me → acknowledged set
  
  console.log('snapKey:', snapKey, 'assignKey:', assignKey);

  const readSeen = useCallback(() => {
    if (!snapKey) return null;
    try { return JSON.parse(localStorage.getItem(snapKey) || '{}'); } catch { return {}; }
  }, [snapKey]);
  const writeSeen = useCallback((map) => {
    if (snapKey) localStorage.setItem(snapKey, JSON.stringify(map));
  }, [snapKey]);

  const readAssignedSeen = useCallback(() => {
    if (!assignKey) return null;   // null = "not ready yet", not "nothing seen"
    try { return JSON.parse(localStorage.getItem(assignKey) || '{}'); } catch { return {}; }
  }, [assignKey]);
 const writeAssignedSeen = useCallback((map) => {
  console.log('%c[writeAssignedSeen] CALLED', 'color: orange; font-weight: bold', 'key:', assignKey, 'map:', map);
  if (assignKey) {
    localStorage.setItem(assignKey, JSON.stringify(map));
    console.log('%c[writeAssignedSeen] WROTE, readback:', 'color: lime', localStorage.getItem(assignKey));
  } else {
    console.log('%c[writeAssignedSeen] SKIPPED — assignKey is falsy', 'color: red');
  }
}, [assignKey]);

  /* ---- background poll: status changes on my tickets + new tickets assigned to me ---- */
  const poll = useCallback(async () => {
  if (!user?.email) return;
    // alert('polling...');
    if (!user) return;
console.log(user.email);
    /* (1) tickets I raised → toast + dot on any STATUS change */
    try {
      const data = await apiGet('?scope=mine');
      const list = data.tickets || [];
      setMyTickets(list);

      const seen = readSeen();
      if (seen === null) {
        console.log('[poll] snapKey not ready, skipping mine comparison this pass');
      } else {
        const nextSeen = { ...seen };
        let changed = 0;

        list.forEach((t) => {
          if (!(t.id in seen)) {
            nextSeen[t.id] = t.status;
          } else if (seen[t.id] !== t.status) {
            changed++;
            nextSeen[t.id] = t.status;
            const key = `${t.id}:${t.status}`;
            if (!detectedRef.current.has(key)) {
              detectedRef.current.add(key);
              toast(`Ticket #${t.id} → ${STATUS[t.status]?.label || t.status}`);
            }
          }
        });

        writeSeen(nextSeen);
        setUnseenMine(changed);
      }
    } catch {
      /* silent */
    }

   
    try {
      const data = await apiGet('?scope=assigned');
      const list = data.tickets || [];
      setAssignedTickets(list);
      const openCount = list.filter((t) => t.status === 'open').length;
      setUnseenAssigned(openCount);
    } catch {
      /* silent */
    }

  }, [user, readSeen, writeSeen, readAssignedSeen]);

  useEffect(() => {
  if (!user?.email) return;
  poll();
  const id = setInterval(poll, 30000);
  return () => clearInterval(id);
}, [user?.email, poll]);

  /* mark tickets as seen → clears the red dot. Two forms:
     - acknowledgeLists(mine, assigned): authoritative, uses freshly-fetched data
     - acknowledge(): instant clear from current state (for snappy click feedback) */
  const acknowledgeLists = useCallback((mineList, asgList) => {
    if (Array.isArray(mineList)) {
      const map = { ...(readSeen() || {}) };
      mineList.forEach((t) => { map[t.id] = t.status; });
      writeSeen(map);
      setUnseenMine(0);
    }
    if (Array.isArray(asgList)) {
      const amap = { ...(readAssignedSeen() || {}) };
      asgList.forEach((t) => { amap[t.id] = t.status; });
      writeAssignedSeen(amap);
      setUnseenAssigned(0);
    }
    detectedRef.current.clear();
  }, [readSeen, writeSeen, readAssignedSeen, writeAssignedSeen]);

  const acknowledge = useCallback(() => {
    acknowledgeLists(myTickets, assignedTickets);
  }, [myTickets, assignedTickets, acknowledgeLists]);

  /* ---- fetchers (return the list so callers can act on fresh data) ---- */
  const fetchMine = useCallback(async () => {
    setLoadingMine(true);
    try {
      const data = await apiGet('?scope=mine');
      const list = data.tickets || [];
      setMyTickets(list);
      return list;
    } catch (e) {
      toast(e.message || "Couldn't load your tickets", false);
      return null;
    } finally {
      setLoadingMine(false);
    }
  }, []);

  const fetchAssigned = useCallback(async () => {
  setLoadingAssigned(true);
  try {
    const data = await apiGet('?scope=assigned');
    const list = data.tickets || [];
    setAssignedTickets(list);
    console.log('[fetchAssigned] success, list:', list);
    return list;
  } catch (e) {
    console.log('[fetchAssigned] FAILED:', e.message);
    toast(e.message || "Couldn't load assigned tickets", false);
    return null;   // ← if this happens, acknowledgeLists skips the write entirely
  } finally {
    setLoadingAssigned(false);
  }
}, []);

  const changeStatus = async (id, status) => {
    try {
      await apiPost(`/${id}/status`, { status });
      toast(`Ticket #${id} → ${STATUS[status]?.label || status}`);
      const fresh = await fetchAssigned();
      if (Array.isArray(fresh)) acknowledgeLists(null, fresh);
    } catch (e) {
      toast(e.message || "Couldn't update status", false);
    }
  };

  /* ---- open / close ---- */
  const openPanel = () => {
    setOpen(true);
    if (unseenAssigned > 0) setTab('assigned');
    else if (unseenMine > 0) setTab('mine');
    if (categories.length === 0) {
      apiGet('/categories')
        .then((r) => setCategories(r.categories || []))
        .catch((e) => toast(e.message || "Couldn't load categories", false));
    }
    fetchMine();
    fetchAssigned();
  };
  const closePanel = () => setOpen(false);

  /* ---- raise-form handlers ---- */
  const onCategory = useCallback(async (val) => {
    setCategory(val); setAssignee(''); setAssignees([]);
    if (!val) return;
    setLoadingA(true);
    try {
      const r = await apiGet(`/assignees?category=${encodeURIComponent(val)}`);
      setAssignees(r.assignees || []);
      if ((r.assignees || []).length === 0) toast(`No one is set up on the ${r.team} team yet`, false);
    } catch (e) {
      toast(e.message || "Couldn't load people", false);
    } finally {
      setLoadingA(false);
    }
  }, []);

  const addFiles = (setter, existing) => (e) => {
    const picked = Array.from(e.target.files || []);
    setter([...existing, ...picked]);
    e.target.value = '';
  };
  const removeAt = (setter, arr, i) => () => setter(arr.filter((_, idx) => idx !== i));

  const resetForm = () => {
    setCategory(''); setAssignees([]); setAssignee('');
    setPriority('medium'); setMessage(''); setImages([]); setDocs([]);
  };

  const submit = async () => {
    if (!category) return toast('Pick a category', false);
    if (!assignee) return toast('Choose who to assign this to', false);
    if (!message.trim()) return toast('Describe the issue', false);

    const fd = new FormData();
    fd.append('category', category);
    fd.append('priority', priority);
    fd.append('assignee_id', assignee);
    fd.append('message', message.trim());
    images.forEach((f) => fd.append('images', f));
    docs.forEach((f) => fd.append('documents', f));

    setSubmitting(true);
    try {
      const r = await apiPostForm('', fd);
      toast(`Ticket #${r.ticket?.id} raised`);
      resetForm();
      await fetchMine();
      acknowledge();
      setTab('mine');
    } catch (e) {
      toast(e.message || "Couldn't raise the ticket", false);
    } finally {
      setSubmitting(false);
    }
  };

  /* ---------- styles ---------- */
  const iconBtn = {
    width: isMobile ? 32 : 36, height: isMobile ? 32 : 36, borderRadius: '50%',
    background: '#f5f3ef', border: 'none', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    position: 'relative', flexShrink: 0,
  };
  const label = {
    display: 'block', fontSize: 11, fontWeight: 600, color: '#6b7280',
    marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px',
  };
  const input = {
    width: '100%', padding: '9px 12px', border: '1px solid #e8e4df', borderRadius: 8,
    fontSize: 14, fontFamily: "'DM Sans', sans-serif", outline: 'none',
    boxSizing: 'border-box', background: '#fff', color: '#1f2937',
  };

  /* shared card renderer for both list tabs */
  const renderTicketCard = (t, opts = {}) => {
    const isOpen = !!expanded[t.id];
    const NEXT = {
      open:        [['in_progress', 'Start'], ['resolved', 'Resolve']],
      in_progress: [['resolved', 'Resolve']],
      resolved:    [['closed', 'Close'], ['open', 'Reopen']],
      closed:      [['open', 'Reopen']],
    };
    return (
      <div key={t.id} style={{ border: '1px solid #eef0f3', borderRadius: 12, marginBottom: 10, overflow: 'hidden' }}>
        <button onClick={() => setExpanded((o) => ({ ...o, [t.id]: !o[t.id] }))} style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 10, background: '#fff', border: 0, padding: '12px 14px', cursor: 'pointer', textAlign: 'left',
        }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <strong style={{ color: '#101F40', fontSize: 14 }}>#{t.id}</strong>
            <span style={{ fontSize: 13, color: '#374151' }}>{t.category_label}</span>
            <Pill map={PRIORITY} k={t.priority} />
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Pill map={STATUS} k={t.status} />
            <span style={{ color: '#9ca3af', fontSize: 12 }}>{isOpen ? '▾' : '▸'}</span>
          </span>
        </button>

        {isOpen && (
          <div style={{ padding: '4px 14px 14px', background: '#fafbfc', display: 'flex', flexDirection: 'column', gap: 10 }}>
            <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.55, whiteSpace: 'pre-wrap', color: '#374151' }}>{t.message}</p>

            {(t.attachments || []).length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {t.attachments.map((a) => (
                  <a key={a.id} href={`${API}/${t.id}/attachments/${a.id}`} target="_blank" rel="noreferrer"
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6, textDecoration: 'none',
                      fontSize: 12.5, fontWeight: 600, color: '#1f2937', background: '#eef2f7',
                      border: '1px solid #e8e4df', padding: '6px 11px', borderRadius: 9,
                    }}>
                    {a.kind === 'image' ? '🖼' : '📄'} {a.original_name || a.filename}
                  </a>
                ))}
              </div>
            )}

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, fontSize: 12.5, color: '#6b7280' }}>
              {opts.showRaiser
                ? <span>Raised by <strong style={{ color: '#1f2937' }}>{t.raised_by_name || '—'}</strong></span>
                : <span>Assigned to <strong style={{ color: '#1f2937' }}>{t.assignee_name || '—'}</strong></span>}
              {t.team && <span>{t.team} team</span>}
              <span>Raised {(t.created_at || '').slice(0, 10)}</span>
            </div>

            {/* status actions — only in the Assigned tab */}
            {opts.canActOnStatus && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
                {(NEXT[t.status] || []).map(([next, lbl]) => (
                  <button key={next} onClick={() => changeStatus(t.id, next)} style={{
                    appearance: 'none', cursor: 'pointer', border: '1px solid #101F40',
                    background: next === 'resolved' || next === 'closed' ? '#101F40' : '#fff',
                    color: next === 'resolved' || next === 'closed' ? '#fff' : '#101F40',
                    borderRadius: 8, padding: '7px 14px', font: "600 13px 'DM Sans'",
                  }}>{lbl}</button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {/* ── the icon (place after the bell) ── */}
      <button style={iconBtn} onClick={openPanel}
        title={unseen > 0 ? `${unseen} ticket update(s)` : 'Support tickets'}>
        <svg viewBox="0 0 24 24" width={isMobile ? 15 : 22} height={isMobile ? 15 : 22}
          fill="none" stroke="#555" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 9a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2 2 2 0 0 0 0 4 2 2 0 0 1-2 2H5a2 2 0 0 1-2-2 2 2 0 0 0 0-4z" />
          <path d="M13 7v10" strokeDasharray="1 2.4" />
        </svg>
        {unseen > 0 && (
          <span style={{
            position: 'absolute', top: 3, right: 3, minWidth: 8, height: 8,
            background: '#ef4444', borderRadius: '50%', border: '1.5px solid #fff',
          }} />
        )}
      </button>

      {/* ── modal ── */}
      {open && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(26,26,46,0.6)',
          display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center',
          zIndex: 10001, backdropFilter: 'blur(4px)', padding: isMobile ? 0 : 20,
          fontFamily: "'DM Sans', sans-serif",
        }}>
          <div style={{
            background: '#fff', borderRadius: isMobile ? '20px 20px 0 0' : 18,
            width: '100%', maxWidth: isMobile ? '100%' : 560,
            maxHeight: isMobile ? '92vh' : '88vh', overflow: 'hidden',
            display: 'flex', flexDirection: 'column', boxShadow: '0 24px 60px rgba(0,0,0,0.2)',
          }}>
            {/* header */}
            <div style={{
              padding: isMobile ? '14px 16px' : '18px 22px', borderBottom: '1px solid #f0ede8',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <h2 style={{ margin: 0, fontSize: isMobile ? 16 : 18, fontWeight: 700, color: '#101F40' }}>
                Support tickets
              </h2>
              <button onClick={closePanel} style={{
                background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: '#aaa', lineHeight: 1,
              }}>×</button>
            </div>

            {/* tabs */}
            <div style={{ display: 'flex', gap: 2, borderBottom: '1px solid #f0ede8', padding: '0 16px' }}>
              {[['raise', 'Raise a ticket'], ['mine', 'My tickets'], ['assigned', 'Assigned to me']].map(([k, txt]) => (
                <button key={k} onClick={() => setTab(k)} style={{
                  appearance: 'none', border: 0, background: 'none', padding: '12px 14px',
                  font: "600 14px 'DM Sans'", cursor: 'pointer', whiteSpace: 'nowrap',
                  color: tab === k ? '#2563eb' : '#6b7280',
                  borderBottom: `2px solid ${tab === k ? '#2563eb' : 'transparent'}`, marginBottom: -1,
                }}>{txt}</button>
              ))}
            </div>

            {/* body */}
            <div style={{ padding: isMobile ? '14px 16px' : '18px 22px', overflowY: 'auto' }}>

              {/* ── RAISE ── */}
              {tab === 'raise' && (
                <>
                  <label style={label}>What kind of issue?</label>
                  <select style={input} value={category} onChange={(e) => onCategory(e.target.value)}>
                    <option value="">Select a category…</option>
                    {categories.map((c) => (
                      <option key={c.value} value={c.value}>{c.label} → {c.team} team</option>
                    ))}
                  </select>

                  <label style={{ ...label, marginTop: 16 }}>Assign to</label>
                  <select style={{ ...input, background: (!category || loadingA) ? '#f7f8fa' : '#fff' }}
                    value={assignee} disabled={!category || loadingA}
                    onChange={(e) => setAssignee(e.target.value)}>
                    <option value="">{loadingA ? 'Loading…' : 'Select a person…'}</option>
                    {assignees.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.name}{a.employee_code ? ` (${a.employee_code})` : ''}
                      </option>
                    ))}
                  </select>

                  <label style={{ ...label, marginTop: 16 }}>Priority</label>
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3,1fr)', gap: 10 }}>
                    {['high', 'medium', 'low'].map((p) => {
                      const s = PRIORITY[p];
                      const on = priority === p;
                      return (
                        <button key={p} type="button" onClick={() => setPriority(p)} style={{
                          appearance: 'none', cursor: 'pointer', textAlign: 'left', background: '#fff',
                          border: `1px solid ${on ? '#101F40' : '#e8e4df'}`,
                          boxShadow: on ? 'inset 0 0 0 1px #101F40' : 'none',
                          borderRadius: 12, padding: '10px 12px', position: 'relative', overflow: 'hidden',
                        }}>
                          <span style={{
                            position: 'absolute', left: 0, top: 10, bottom: 10, width: 3,
                            borderRadius: 3, background: s.rail,
                          }} />
                          <span style={{ display: 'block', fontWeight: 700, fontSize: 14, color: s.color, paddingLeft: 8 }}>{s.label}</span>
                          <span style={{ display: 'block', fontSize: 11, color: '#6b7280', marginTop: 2, paddingLeft: 8 }}>{s.sla}</span>
                        </button>
                      );
                    })}
                  </div>

                  <label style={{ ...label, marginTop: 16 }}>Describe the issue</label>
                  <textarea style={{ ...input, minHeight: 90, resize: 'vertical', lineHeight: 1.5 }}
                    rows={5} value={message} onChange={(e) => setMessage(e.target.value)}
                    placeholder="What happened, and what do you need?" />

                  {/* uploads */}
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16, marginTop: 16 }}>
                    <div>
                      <label style={label}>Screenshots (optional)</label>
                      <label style={{
                        display: 'inline-block', cursor: 'pointer', font: "600 13px 'DM Sans'",
                        color: '#2563eb', border: '1px dashed #b9c6e6', background: '#f5f8ff',
                        padding: '9px 14px', borderRadius: 10,
                      }}>
                        + Add images
                        <input type="file" accept={IMAGE_ACCEPT} multiple hidden onChange={addFiles(setImages, images)} />
                      </label>
                      {images.length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
                          {images.map((f, i) => (
                            <div key={i} style={{ position: 'relative', width: 60, height: 60, borderRadius: 10, overflow: 'hidden', border: '1px solid #e8e4df' }}>
                              <img alt={f.name} src={URL.createObjectURL(f)} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                              <button type="button" onClick={removeAt(setImages, images, i)} style={{
                                position: 'absolute', top: 2, right: 2, width: 18, height: 18, border: 0,
                                borderRadius: '50%', background: '#000a', color: '#fff', cursor: 'pointer', fontSize: 12, lineHeight: 1,
                              }}>×</button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div>
                      <label style={label}>Documents (optional)</label>
                      <label style={{
                        display: 'inline-block', cursor: 'pointer', font: "600 13px 'DM Sans'",
                        color: '#2563eb', border: '1px dashed #b9c6e6', background: '#f5f8ff',
                        padding: '9px 14px', borderRadius: 10,
                      }}>
                        + Add documents
                        <input type="file" accept={DOC_ACCEPT} multiple hidden onChange={addFiles(setDocs, docs)} />
                      </label>
                      {docs.length > 0 && (
                        <ul style={{ listStyle: 'none', margin: '10px 0 0', padding: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {docs.map((f, i) => (
                            <li key={i} style={{
                              display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13,
                              background: '#f7f8fa', border: '1px solid #e8e4df', borderRadius: 9, padding: '7px 11px',
                            }}>
                              <span style={{ wordBreak: 'break-all' }}>📄 {f.name}</span>
                              <button type="button" onClick={removeAt(setDocs, docs, i)} style={{
                                border: 0, background: 'none', color: '#c0392b', cursor: 'pointer', font: "600 12px 'DM Sans'", flexShrink: 0,
                              }}>Remove</button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: 10, marginTop: 22 }}>
                    <button onClick={submit} disabled={submitting} style={{
                      flex: 1, padding: 12, background: submitting ? '#d1d5db' : '#101F40', color: '#fff',
                      border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600,
                      cursor: submitting ? 'not-allowed' : 'pointer', fontFamily: "'DM Sans', sans-serif",
                    }}>{submitting ? 'Raising…' : 'Raise ticket'}</button>
                    <button onClick={resetForm} disabled={submitting} style={{
                      padding: '12px 18px', background: '#fff', color: '#6b7280', border: '1px solid #e8e4df',
                      borderRadius: 10, fontSize: 14, fontWeight: 500, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
                    }}>Clear</button>
                  </div>
                </>
              )}

              {/* ── MY TICKETS ── */}
              {tab === 'mine' && (
                <>
                  {loadingMine && myTickets.length === 0 && (
                    <p style={{ color: '#9ca3af', fontSize: 13, textAlign: 'center', padding: 24 }}>Loading…</p>
                  )}
                  {!loadingMine && myTickets.length === 0 && (
                    <div style={{ textAlign: 'center', padding: '40px 16px', color: '#9ca3af' }}>
                      <div style={{ fontSize: 34, marginBottom: 10 }}>🎫</div>
                      <p style={{ margin: 0, fontSize: 14 }}>You haven't raised any tickets yet.</p>
                    </div>
                  )}
                  {myTickets.map((t) => renderTicketCard(t, { showRaiser: false, canActOnStatus: false }))}
                </>
              )}

              {/* ── ASSIGNED TO ME ── */}
              {tab === 'assigned' && (
                <>
                  {loadingAssigned && assignedTickets.length === 0 && (
                    <p style={{ color: '#9ca3af', fontSize: 13, textAlign: 'center', padding: 24 }}>Loading…</p>
                  )}
                  {!loadingAssigned && assignedTickets.length === 0 && (
                    <div style={{ textAlign: 'center', padding: '40px 16px', color: '#9ca3af' }}>
                      <div style={{ fontSize: 34, marginBottom: 10 }}>📥</div>
                      <p style={{ margin: 0, fontSize: 14 }}>No tickets are assigned to you.</p>
                    </div>
                  )}
                  {assignedTickets.map((t) => renderTicketCard(t, { showRaiser: true, canActOnStatus: true }))}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
